import { useEffect, useMemo, useState } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../lib/auth";
import { LOGO_URL } from "../lib/brand";
import { getStoredRefSlug, clearStoredRefSlug } from "../lib/refCapture";
import { useT } from "../i18n/LanguageContext";
import { AlertCircle, Check, Eye, EyeOff, Loader2, ShieldCheck, UserPlus, Lightbulb } from "lucide-react";
import { MailDeliveryHint } from "../components/MailDeliveryHint";
import { log } from "../lib/log";
import { suggestEmailFix } from "../lib/emailTypo";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function Register() {
  const { partnerSlug: urlSlug } = useParams();
  const partnerSlug = urlSlug || getStoredRefSlug() || null;
  const navigate = useNavigate();
  const { register } = useAuth();
  const { lang } = useT();
  const [partner, setPartner] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(!!partnerSlug);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", birthdate: "", password: "", password_confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [success, setSuccess] = useState(false);
  // Detect common e-mail-domain typos (gmai.com → gmail.com) so customers
  // don't end up creating duplicate accounts that admins later have to merge.
  const emailSuggestion = useMemo(() => suggestEmailFix(form.email), [form.email]);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  usePageSEO({ title: "Konto erstellen · Alphalane", noIndex: true });

  const loadPartner = async (slug) => {
    if (!slug) { setPartnerLoading(false); return; }
    setPartnerLoading(true);
    try {
      const r = await axios.get(`${API}/auth/partner/${slug}`);
      setPartner(r.data);
    } catch {
      setPartner({ slug, name: null });
    } finally {
      setPartnerLoading(false);
    }
  };

  useEffect(() => {
    loadPartner(partnerSlug);
  }, [partnerSlug]);

  // Direct inline setForm calls in each onChange handler below — we
  // intentionally avoid a `setField` helper because the lint tool's
  // react-hooks/set-state-in-effect rule misflags any function in the
  // component body that calls setState.

  // Normalise password before comparing/submitting. iOS Safari sometimes
  // appends a trailing whitespace when the user uses Auto-Suggest / "Strong
  // Password" or copy-pastes between the two fields — both visibly show
  // "abc…" but one secretly carries a trailing space, breaking the
  // strict equality check. Also strip zero-width characters (U+200B and
  // friends) that some keyboards inject.
  const normalizePw = (s) =>
    String(s || "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+$/g, "")
      .replace(/^\s+/g, "");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const pw = normalizePw(form.password);
    const pw2 = normalizePw(form.password_confirm);
    if (pw !== pw2) {
      // Give a more actionable hint when the user clearly meant the same
      // password but iOS or copy-paste introduced invisible chars.
      const hint = (pw.length !== pw2.length)
        ? " (Tipp: häufig ein versehentliches Leerzeichen am Anfang oder Ende — beide Felder neu eintippen.)"
        : "";
      setError(`Passwörter stimmen nicht überein.${hint}`);
      return;
    }
    if (pw.length < 8) { setError("Passwort muss mindestens 8 Zeichen lang sein."); return; }
    setBusy(true);
    try {
      const data = await register({ ...form, password: pw, password_confirm: pw, partner_slug: partnerSlug || null, mail_language: lang === "en" ? "en" : "de" });
      clearStoredRefSlug();
      toast.success("Registrierung erfolgreich!");
      // Persist contract token and forward to the Vertrags-Step right away.
      if (data?.contract_token) {
        try {
          localStorage.setItem("alphalane_contract_token", data.contract_token);
        } catch (err) {
          log.warn("[register] contract_token persist failed:", err);
        }
        navigate(`/onboarding/contract?token=${encodeURIComponent(data.contract_token)}`);
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Registrierung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <Shell>
        <div className="al-card p-8 sm:p-10 text-center" data-testid="register-success">
          <div className="mx-auto h-14 w-14 rounded-full al-grad-bg text-white flex items-center justify-center mb-5">
            <Check className="h-7 w-7" />
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl">Registrierung erfolgreich!</h1>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
            Vielen Dank für deine Anmeldung. Dein Zugang wird in Kürze manuell freigeschaltet —
            du erhältst eine Bestätigungsmail, sobald du loslegen kannst.
          </p>
          <MailDeliveryHint testid="register-mail-hint" />
          <Link to="/" className="al-cta mt-6 inline-flex" data-testid="register-back">Zur Website</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="al-card p-7 sm:p-9" data-testid="register-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl al-grad-bg text-white flex items-center justify-center">
            <UserPlus className="h-5 w-5" />
          </div>
          <div>
            <span className="al-tag">Konto erstellen</span>
            <h1 className="mt-1 font-display font-bold text-2xl sm:text-3xl">Willkommen bei Alphalane.</h1>
          </div>
        </div>

        {partnerSlug && (
          <div className="mb-6 rounded-xl border border-[var(--al-purple)]/20 bg-[var(--al-grad-soft)] al-grad-soft-bg px-4 py-3 flex items-start gap-3" data-testid="partner-banner">
            <ShieldCheck className="h-5 w-5 text-[var(--al-purple)] flex-shrink-0 mt-0.5" />
            <div className="text-sm flex-1 min-w-0">
              {partnerLoading ? (
                <div className="text-slate-600">Prüfe Empfehlung…</div>
              ) : partner?.name ? (
                <>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--al-purple)]">Empfohlen durch</div>
                  <div className="font-display font-semibold text-slate-900 text-base mt-0.5" data-testid="partner-name">{partner.name}</div>
                  <div className="text-[11px] text-slate-500 mt-1 font-mono-tab">Partner-ID: {partnerSlug}</div>
                </>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--al-purple)]">Empfehlung von</div>
                  <div className="font-display font-semibold text-slate-900 text-base mt-0.5 font-mono-tab" data-testid="partner-name-fallback">{partnerSlug}</div>
                </>
              )}
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Vorname" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} required testid="reg-first-name" />
            <Input label="Nachname" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} required testid="reg-last-name" />
          </div>
          <Input type="email" label="E-Mail" value={form.email} onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setSuggestionDismissed(false); }} required testid="reg-email" />
          {emailSuggestion && !suggestionDismissed && (
            <div
              className="-mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-900 flex items-start gap-2"
              data-testid="reg-email-typo-warning"
            >
              <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <div className="flex-1 leading-relaxed">
                Meintest du <strong className="font-display font-bold" data-testid="reg-email-typo-suggest">{emailSuggestion.suggestion}</strong>?
                <div className="mt-1.5 flex gap-2">
                  <button
                    type="button"
                    data-testid="reg-email-typo-accept"
                    onClick={() => { setForm((f) => ({ ...f, email: emailSuggestion.suggestion })); setSuggestionDismissed(true); }}
                    className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-[12px] font-semibold hover:bg-amber-700"
                  >
                    Ja, übernehmen
                  </button>
                  <button
                    type="button"
                    data-testid="reg-email-typo-dismiss"
                    onClick={() => setSuggestionDismissed(true)}
                    className="px-2.5 py-1 rounded-md border border-amber-300 text-amber-800 text-[12px] font-semibold hover:bg-amber-100"
                  >
                    Nein, ist korrekt
                  </button>
                </div>
              </div>
            </div>
          )}
          <Input type="tel" label="Telefonnummer" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} required testid="reg-phone" placeholder="+41 79 123 45 67" />
          <Input type="date" label="Geburtsdatum" value={form.birthdate} onChange={(e) => setForm((f) => ({ ...f, birthdate: e.target.value }))} required testid="reg-birthdate" />
          <PasswordField label="Passwort" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} show={showPw} onToggle={() => setShowPw(!showPw)} testid="reg-password" />
          <PasswordField label="Passwort bestätigen" value={form.password_confirm} onChange={(e) => setForm((f) => ({ ...f, password_confirm: e.target.value }))} show={showPw} onToggle={() => setShowPw(!showPw)} testid="reg-password-confirm" />

          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2" data-testid="register-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={busy} className="al-cta w-full justify-center" data-testid="register-submit">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Erstelle Konto…</> : <>Konto erstellen <UserPlus className="h-4 w-4" /></>}
          </button>

          <p className="text-xs text-slate-500 text-center">
            Mit der Registrierung akzeptierst du unsere <Link to="/agb" className="text-[var(--al-purple)] hover:underline">AGB</Link> und <Link to="/datenschutz" className="text-[var(--al-purple)] hover:underline">Datenschutzerklärung</Link>.
          </p>
        </form>

        <div className="mt-7 pt-6 border-t border-[var(--al-line)] text-center text-sm text-slate-600">
          Bereits ein Konto? <Link to="/login" className="text-[var(--al-purple)] font-semibold hover:underline" data-testid="link-login">Hier einloggen →</Link>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[var(--al-bg)] flex flex-col" data-testid="auth-shell">
    <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
          <div className="font-display font-semibold text-[15px]"><span className="al-grad-text">ALPHALANE</span></div>
        </Link>
      </div>
    </header>
    <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10">
      <div className="w-full max-w-lg">{children}</div>
    </main>
  </div>
);

const Input = ({ label, type = "text", value, onChange, required, testid, placeholder }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1.5 w-full rounded-xl border border-[var(--al-line)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20 transition-all"
    />
  </label>
);

const PasswordField = ({ label, value, onChange, show, onToggle, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</span>
    <div className="mt-1.5 relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        required
        minLength={8}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-testid={testid}
        className="w-full rounded-xl border border-[var(--al-line)] bg-white px-4 py-2.5 pr-11 text-sm focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20 transition-all"
      />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </label>
);
